import { useParams, Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Check, X } from 'lucide-react';

const competitorData = {
  'lunchbreak': {
    name: 'Lunchbreak AI',
    description: 'AI text humanizer focused on bypassing detection tools',
    ourWins: [
      'Actually works - proven bypass rates',
      'Real free tier (5,000 words, no CC)',
      'Faster processing speed (3-5 seconds vs 10-15)',
      '3.3x more words on paid plans (50k vs 15k)',
      'Detailed detector scoring with percentages',
      'Full history dashboard with search',
      'Multiple humanization styles',
      'Bulk processing capability'
    ],
    theirWins: [
      'Been around longer (established 2023)',
      'Larger marketing presence',
      'More reviews online'
    ],
    pricing: {
      them: 'R359/mo for 15,000 words',
      us: 'R359/mo for 50,000 words',
      winner: 'Penocio'
    },
    features: [
      { feature: 'Free tier words', them: '2,000 (CC required)', us: '5,000 (no CC)', winner: 'us' },
      { feature: 'Monthly plan words', them: '15,000', us: '50,000', winner: 'us' },
      { feature: 'Processing speed', them: '10-15 seconds', us: '3-5 seconds', winner: 'us' },
      { feature: 'Detector scoring', them: 'Basic', us: 'Detailed %', winner: 'us' },
      { feature: 'History dashboard', them: 'Limited', us: 'Full access', winner: 'us' },
      { feature: 'API access', them: 'No', us: 'Yes (paid)', winner: 'us' }
    ]
  },
  'undetectable': {
    name: 'Undetectable AI',
    description: 'Popular AI humanization service',
    ourWins: [
      'Better pricing (R359 vs $24.99)',
      'More words per month (50k vs 30k)',
      'Real free tier (no CC required)',
      'Faster processing',
      'Cleaner, more intuitive UI',
      'Better customer support'
    ],
    theirWins: [
      'More established brand',
      'Larger user base',
      'More marketing content'
    ],
    pricing: {
      them: '$24.99/mo for 30,000 words',
      us: 'R359/mo for 50,000 words',
      winner: 'Penocio'
    },
    features: [
      { feature: 'Monthly plan price', them: '$24.99', us: 'R359', winner: 'us' },
      { feature: 'Monthly plan words', them: '30,000', us: '50,000', winner: 'us' },
      { feature: 'Free tier', them: 'Trial only', us: '5,000 words/mo', winner: 'us' },
      { feature: 'Processing speed', them: 'Moderate', us: 'Fast', winner: 'us' }
    ]
  },
  'quillbot': {
    name: 'QuillBot',
    description: 'Popular paraphrasing and writing tool',
    ourWins: [
      'Specifically designed for AI detection bypass',
      'Better at humanizing AI text',
      'Multi-detector scoring included',
      'More affordable for students',
      'Focused on detector bypass, not just paraphrasing'
    ],
    theirWins: [
      'More features (grammar, summarizer)',
      'Well-established brand since 2017',
      'Browser extension available',
      'Multiple writing tools in one'
    ],
    pricing: {
      them: '$19.95/mo (paraphraser only)',
      us: 'R359/mo (full AI humanization)',
      winner: 'Tie'
    },
    features: [
      { feature: 'AI detection bypass', them: 'No', us: 'Yes', winner: 'us' },
      { feature: 'Detector scoring', them: 'No', us: 'Yes', winner: 'us' },
      { feature: 'Word limit', them: 'Varies', us: '50,000/mo', winner: 'us' },
      { feature: 'Grammar tools', them: 'Yes', us: 'No', winner: 'them' }
    ]
  },
  'hix': {
    name: 'HIX Bypass',
    description: 'AI humanization and bypass tool',
    ourWins: [
      'Better pricing and value',
      'More transparent pricing',
      'Cleaner interface',
      'Faster processing',
      'Real free tier'
    ],
    theirWins: [
      'Multiple product integrations',
      'Wider AI tool ecosystem'
    ],
    pricing: {
      them: 'Contact for pricing',
      us: 'R359/mo for 50,000 words',
      winner: 'Penocio'
    },
    features: [
      { feature: 'Transparent pricing', them: 'No', us: 'Yes', winner: 'us' },
      { feature: 'Free tier', them: 'Limited', us: '5,000 words/mo', winner: 'us' }
    ]
  },
  'stealth': {
    name: 'StealthWriter',
    description: 'AI detection bypass service',
    ourWins: [
      'Better word allowance',
      'More affordable pricing',
      'Better UI/UX',
      'Faster results'
    ],
    theirWins: [
      'Multiple plan options',
      'Niche focus'
    ],
    pricing: {
      them: '$20/mo for 25,000 words',
      us: 'R359/mo for 50,000 words',
      winner: 'Penocio'
    },
    features: [
      { feature: 'Words per month', them: '25,000', us: '50,000', winner: 'us' },
      { feature: 'Price', them: '$20', us: 'R359', winner: 'us' }
    ]
  },
  'humbot': {
    name: 'Humbot',
    description: 'AI to human text converter',
    ourWins: [
      'Better value for money',
      'More words included',
      'Better detection rates',
      'Superior dashboard'
    ],
    theirWins: [
      'Simple branding'
    ],
    pricing: {
      them: 'R359/mo for 20,000 words',
      us: 'R359/mo for 50,000 words',
      winner: 'Penocio'
    },
    features: [
      { feature: 'Words per month', them: '20,000', us: '50,000', winner: 'us' }
    ]
  }
};

const Versus = () => {
  const { competitor } = useParams();
  const data = competitorData[competitor] || {
    name: 'Competitor',
    description: 'AI humanization tool',
    ourWins: [
      'More accurate humanization',
      'Better pricing',
      'Faster processing',
      'Real free tier'
    ],
    theirWins: [
      'Established brand'
    ],
    pricing: {
      them: 'Contact for details',
      us: 'R359/mo for 50,000 words',
      winner: 'Penocio'
    },
    features: []
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <div className="flex-1 py-24">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-16">
            <Badge className="mb-4" variant="secondary">Comparison</Badge>
            <h1 className="text-4xl md:text-5xl font-heading font-black tracking-tighter mb-6">
              Penocio vs {data.name}
            </h1>
            <p className="text-lg text-muted-foreground">
              An honest comparison of features, pricing, and performance.
            </p>
          </div>

          {/* Feature Comparison Table */}
          {data.features && data.features.length > 0 && (
            <Card className="p-8 border-zinc-200 mb-12">
              <h2 className="text-2xl font-heading font-bold mb-6">Feature Comparison</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-200">
                      <th className="text-left py-3 font-heading">Feature</th>
                      <th className="text-left py-3 font-heading">{data.name}</th>
                      <th className="text-left py-3 font-heading">Penocio</th>
                      <th className="text-center py-3 font-heading">Winner</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.features.map((row) => (
                      <tr key={row.feature} className="border-b border-zinc-200">
                        <td className="py-3 font-medium">{row.feature}</td>
                        <td className="py-3 text-muted-foreground">{row.them}</td>
                        <td className="py-3 text-muted-foreground">{row.us}</td>
                        <td className="py-3 text-center">
                          <Badge variant={row.winner === 'us' ? 'default' : 'outline'}>
                            {row.winner === 'us' ? 'Penocio' : data.name}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Comparison Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <Card className="p-8 border-2 border-primary">
              <h2 className="text-2xl font-heading font-bold mb-6">Where Penocio Wins</h2>
              <ul className="space-y-3">
                {data.ourWins.map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="p-8 border-zinc-200">
              <h2 className="text-2xl font-heading font-bold mb-6">Where {data.name} Wins</h2>
              <ul className="space-y-3">
                {data.theirWins.map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          {/* Pricing Comparison */}
          <Card className="p-8 border-zinc-200 mb-12">
            <h2 className="text-2xl font-heading font-bold mb-6">Pricing Comparison</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-heading font-bold mb-2">Penocio</h3>
                <p className="text-2xl font-bold mb-2">{data.pricing.us}</p>
                <Badge variant="default">Best Value</Badge>
              </div>
              <div>
                <h3 className="font-heading font-bold mb-2">{data.name}</h3>
                <p className="text-2xl font-bold mb-2">{data.pricing.them}</p>
              </div>
            </div>
          </Card>

          {/* CTA */}
          <Card className="p-12 text-center bg-secondary border-zinc-200">
            <h2 className="text-3xl font-heading font-bold mb-4">Try Penocio Free</h2>
            <p className="text-muted-foreground mb-6">No credit card required. 5,000 words to start.</p>
            <Link to="/">
              <Button size="lg" data-testid="versus-cta-btn">Get Started Free</Button>
            </Link>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Versus;
